"use client";
import {Button, Spin} from "antd";
import clsx from "clsx";
import {Fragment, lazy, ReactNode, useEffect, useState} from "react";
import {useUserStore} from "@/lib/store/user.store";

const RestPassword = lazy(() => import("@/components/user/rest-password"));
const RestMobile = lazy(() => import("@/components/user/rest-mobile"));
const ChangePayPwd = lazy(() => import("@/components/pay/change-pay-pwd"));

type DataType = {
    icon: string;
    title: string;
    status: boolean;
    key: string;
    statusNode: ReactNode;
    desc: string;
    button: (onClick: () => void) => ReactNode;
}

// 判断账户安全等级  status 为true 的数量低于data.length的 30% 为低  31-60 为中  61-100 为高
const getSecurityLevel = (data: DataType[]) => {
    const total = data.length;
    const statusTrue = data.filter((item) => item.status).length;
    if (statusTrue < total * 0.3) {
        return "低";
    } else if (statusTrue < total * 0.99) {
        return "中";
    } else {
        return "高";
    }
};
export default function SecurityServices() {
    const {userInfo} = useUserStore();
    const [openResetPwd, setOpenResetPw] = useState(false);
    const [openRestMobile, setOpenRestMobile] = useState(false);
    const [openChangePayPwd, setOpenChangePayPwd] = useState(false);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<DataType[]>([{
        icon: "ri-lock-2-line",
        title: "登录密码",
        key: "password_hash",
        status: false,
        statusNode: <p className="text-[10px] text-green-600">未设置</p>,
        desc: "安全性高的密码可以使账号更安全。建议您定期更换密码，且设置一个包含数字和字母，并修改密码长度超过6位以上的密码，为保证你的账户安全，只有在绑定邮箱或手机后才可以修改密码。",
        button: (onClick: () => void) => <Button onClick={onClick}>设置</Button>
    }, {
        icon: "ri-mail-line",
        title: "邮箱绑定",
        key: "email",
        status: false,
        statusNode: <p className="text-[10px] text-red-500">未设置</p>,
        desc: "进行邮箱验证后，可用于接收敏感操作的身份验证信息，以及订阅更优惠商品的促销邮件。",
        button: (onClick: () => void) => <Button onClick={onClick}>设置</Button>
    }, {
        icon: "ri-smartphone-line",
        title: "手机绑定",
        key: "mobile",
        status: false,
        statusNode: <p className="text-[10px] text-green-600">未设置</p>,
        desc: "进行手机验证后，可用于接收敏感操作的身份验证信息，以及接收订单进度详情短信。",
        button: (onClick: () => void) => <Button onClick={onClick}>设置</Button>
    }, {
        icon: "ri-secure-payment-line",
        title: "支付密码",
        key: "payHash",
        status: false,
        statusNode: <p className="text-[10px] text-red-500">未设置</p>,
        desc: "设置支付密码后，在使用账户中余额时，需输入支付密码。",
        button: (onClick: () => void) => <Button onClick={onClick}>设置</Button>
    }]);

    useEffect(() => {
        if (userInfo) {
            setLoading(false);
            setData((old) => {
                return old.map(item => {
                    const status = !!userInfo.member[item.key];
                    return {
                        ...item,
                        status,
                        statusNode: status ? <p className="text-[10px] text-green-600">已设置</p> :
                            <p className="text-[10px] text-red-500">未设置</p>,
                        button: status ? (onClick: () => void) => <Button onClick={onClick}>修改</Button> :
                            (onClick: () => void) => <Button onClick={onClick} type="primary">设置</Button>
                    };
                });
            });
        }
    }, [userInfo]);
    return <div className="mt-16">
        <Spin spinning={loading}>
            <p className="text-main text-[12px] px-3 py-1 bg-[#ffefe5] font-bold">您的安全服务</p>
            <div className="my-4">
                <p><span>当前安全等级：</span><strong className="text-main text-[20px]">{getSecurityLevel(data)}</strong>
                    {
                        getSecurityLevel(data) !== "高" &&
                        <span className="text-xs text-main">（建议您开启安全设置，以保障账户及资金安全）</span>
                    }
                </p>
            </div>
            <div>
                {
                    data.map((item, index) => {
                        return <div className="b-flex border-t py-3 last:border-b" key={index}>
                            <div className="s-flex gap-x-6" key={index}>
                                <i className={clsx("ri-3x text-main", item.icon)}></i>
                                <div className="flex-col c-flex gap-y-1 flex-shrink-0">
                                    <p className="font-bold text-[14px]">{item.title}</p>
                                    <Fragment>{item.statusNode}</Fragment>
                                </div>
                                <p className="text-themeP text-[12px]">
                                    {item.desc}
                                </p>
                            </div>
                            <Fragment>
                                {item.button(() => {
                                    if (item.key === "password_hash") {
                                        setOpenResetPw(true);
                                    } else if (item.key === "mobile") {
                                        setOpenRestMobile(true);
                                    } else if (item.key === "payHash") {
                                        setOpenChangePayPwd(true);
                                    }
                                })}
                            </Fragment>
                        </div>;
                    })
                }
            </div>
        </Spin>
        <RestPassword open={openResetPwd} setOpen={setOpenResetPw}/>
        <RestMobile open={openRestMobile} setOpen={setOpenRestMobile}/>
        <ChangePayPwd open={openChangePayPwd} setOpen={setOpenChangePayPwd}/>
    </div>;
}
